<template>
  <div class="app-customer-address">
    <h2 class=" justify-space-between">
      <span class="text-3xl">Anschrift</span>
      <button v-if="currentCustomer && !isEditing" @click="startEditing()" class="change-personal-data-button text-xs">
        <img src="/files/lidl/images/new-icons/svg/pen-outline.svg" />
        <span>Angaben ändern</span>
      </button>
    </h2>
    <slot name="txtBefore"></slot>
    <div v-if="!isEditing">
      <p>{{ currentCustomer.salutation }} {{ currentCustomer.firstName }} {{ currentCustomer.lastName }}</p>
      <p>{{ currentCustomer.address.street }} {{ currentCustomer.address.houseNumber }}</p>
      <p>{{ currentCustomer.address.zip }} {{ currentCustomer.address.city }}</p>
    </div>
    <div v-else>
      <form @submit.prevent="saveData()" class="content-limiter">
        <fieldset class="inputs-container ">
          <legend class="text-xl visually-hidden">Adressdaten</legend>
          <!-- Straße & Hausnummer -->
          <div class="input-group">
            <div class="input-field input-field--fixed">
              <label class="text-base-medium" for="street"> Straße<span class="required" aria-hidden="true">*</span> </label>
              <ValidationIndicator
                :dirty="$v.currentCustomer.address.street.$dirty"
                :valid="isValid('street', 'address', 'currentCustomer')"
              >
                <input
                  id="street"
                  name="street"
                  placeholder="Straße"
                  type="text"
                  class="input"
                  v-model="currentCustomer.address.street"
                  required
                  @blur="$v.currentCustomer.address.street.$touch"
                  @click="$v.currentCustomer.address.street.$reset"
                  :class="{
                    'input--invalid':
                      $v.currentCustomer.address.street.$dirty && !isValid('street', 'address', 'currentCustomer'),
                    'input--valid': $v.currentCustomer.address.street.$dirty && isValid('street', 'address', 'currentCustomer')
                  }"
                  aria-describedby="street-hint"
                  aria-errormessage="street-error"
                />
              </ValidationIndicator>
              <small id="street-hint" class="sr-only">Pflichtfeld</small>
              <div
                id="street-error"
                class="error-feedback"
                v-if="!isValid('street', 'address', 'currentCustomer') && $v.currentCustomer.address.street.$dirty"
              >
                <span v-if="$v.currentCustomer.address.street.required === false"> Bitte gib deine Straße ein.<br /> </span>
              </div>
            </div>

            <div class="input-field input-field--fluid">
              <label class="text-base-medium" for="houseNumber">
                Hausnummer<span class="required" aria-hidden="true">*</span>
              </label>
              <ValidationIndicator
                :dirty="$v.currentCustomer.address.houseNumber.$dirty"
                :valid="isValid('houseNumber', 'address', 'currentCustomer')"
              >
                <input
                  id="houseNumber"
                  name="houseNumber"
                  placeholder="Hausnummer"
                  type="text"
                  class="input"
                  v-model="currentCustomer.address.houseNumber"
                  required
                  @blur="$v.currentCustomer.address.houseNumber.$touch"
                  @click="$v.currentCustomer.address.houseNumber.$reset"
                  :class="{
                    'input--invalid':
                      $v.currentCustomer.address.houseNumber.$dirty && !isValid('houseNumber', 'address', 'currentCustomer'),
                    'input--valid':
                      $v.currentCustomer.address.houseNumber.$dirty && isValid('houseNumber', 'address', 'currentCustomer')
                  }"
                  aria-describedby="houseNumber-hint"
                />
              </ValidationIndicator>
              <small id="houseNumber-hint" class="sr-only">Pflichtfeld</small>
              <div
                id="houseNumber-error"
                class="error-feedback"
                v-if="!isValid('houseNumber', 'address', 'currentCustomer') && $v.currentCustomer.address.houseNumber.$dirty"
              >
                <span v-if="$v.currentCustomer.address.houseNumber.required === false">
                  Bitte gib deine Hausnummer ein.“<br />
                </span>
                <span v-if="$v.currentCustomer.address.houseNumber.alphaNumeric === false">
                  Deine Haunsummer darf nur Buchstaben und Zahlen beinhalten<br />
                </span>
                <span v-if="$v.currentCustomer.address.houseNumber.maxLength === false">
                  Deine Hausnummer darf maximal neunstellig sein<br />
                </span>
              </div>
            </div>
          </div>
          <!-- Zusatz -->
          <div class="input-field">
            <label class="text-base-medium" for="supplement">
              Adresszusatz
            </label>
            <input
              id="supplement"
              name="supplement"
              placeholder="Adresszusatz"
              type="text"
              class="input"
              v-model="currentCustomer.address.supplement"
            />
          </div>
          <!-- PLZ & Ort -->
          <div class="input-group">
            <div class="input-field input-field--fluid">
              <label class="text-base-medium flex-align-center" for="zip">
                <abbr title="Postleitzahl">PLZ</abbr><span class="required" aria-hidden="true">*</span>
              </label>
              <ValidationIndicator
                :dirty="$v.currentCustomer.address.zip.$dirty"
                :valid="isValid('zip', 'address', 'currentCustomer')"
              >
                <input
                  id="zip"
                  name="zip"
                  placeholder="PLZ"
                  type="text"
                  class="input"
                  v-model.trim="currentCustomer.address.zip"
                  required
                  @blur="$v.currentCustomer.address.zip.$touch"
                  @click="$v.currentCustomer.address.zip.$reset"
                  :class="{
                    'input--invalid': $v.currentCustomer.address.zip.$dirty && !isValid('zip', 'address', 'currentCustomer'),
                    'input--valid': $v.currentCustomer.address.zip.$dirty && isValid('zip', 'address', 'currentCustomer')
                  }"
                  aria-describedby="zip-hint"
                  aria-errormessage="zip-error"
                />
              </ValidationIndicator>
              <small id="zip-hint" class="sr-only">Pflichtfeld</small>
              <div
                id="zip-error"
                class="error-feedback"
                v-if="!isValid('zip', 'address', 'currentCustomer') && $v.currentCustomer.address.zip.$dirty"
              >
                <span v-if="$v.currentCustomer.address.zip.required === false"> Bitte gib deine Postleitzahl ein.<br /> </span>
                <span v-if="$v.currentCustomer.address.zip.validZipCode === false">
                  Die Postleitzahl darf nur aus Zahlen bestehen und muss fünfstellig sein.<br />
                </span>
              </div>
            </div>

            <div class="input-field input-field--fixed">
              <label class="text-base-medium" for="city"> Ort<span class="required" aria-hidden="true">*</span> </label>
              <ValidationIndicator
                :dirty="$v.currentCustomer.address.city.$dirty"
                :valid="isValid('city', 'address', 'currentCustomer')"
              >
                <input
                  id="city"
                  name="city"
                  placeholder="Ort"
                  type="text"
                  class="input"
                  v-model="currentCustomer.address.city"
                  required
                  @blur="$v.currentCustomer.address.city.$touch"
                  @click="$v.currentCustomer.address.city.$reset"
                  :class="{
                    'input--invalid': $v.currentCustomer.address.city.$dirty && !isValid('city', 'address', 'currentCustomer'),
                    'input--valid': $v.currentCustomer.address.city.$dirty && isValid('city', 'address', 'currentCustomer')
                  }"
                  aria-describedby="city-hint"
                  aria-errormessage="city-error"
                />
              </ValidationIndicator>
              <small id="city-hint" class="sr-only">Pflichtfeld</small>
              <div
                id="city-error"
                class="error-feedback"
                v-if="!isValid('city', 'address', 'currentCustomer') && $v.currentCustomer.address.city.$dirty"
              >
                <span v-if="$v.currentCustomer.address.city.required === false"> Bitte gib deinen Wohnort ein.<br /> </span>
                <span v-if="$v.currentCustomer.address.city.validCityString === false">
                  Der Ortsname darf nur Buchstaben, Leerzeichen und bestimmte Sonderzeichen enthalten.<br />
                </span>
              </div>
            </div>
          </div>
        </fieldset>

        <div class="justify-space-between wrap-mobile">
          <button :disabled="loading" @click="discard()" class="button button--secondary" type="reset">
            Abbrechen
          </button>
          <button :disabled="loading" @click="save()" class="button button--primary" type="button">
            Speichern
          </button>
        </div>
      </form>
    </div>
    <slot name="txtAfter"></slot>
  </div>
</template>

<script>
import transaction from '@/mixins/transaction'
import slotProxy from '../../../../../../mixins/slotProxy'
import gql from 'graphql-tag'
import { wrapQuery } from '../../../../../../helper/wrapGQLQuery'
import { mapActions } from 'vuex'
import { required, alphaNum, maxLength } from 'vuelidate/lib/validators'
import { validZipCode, validCityString } from '@/validators/address'
import { validationMixin } from 'vuelidate'
import isValid from '@/mixins/isValid'
import ValidationIndicator from '@/components-v2/generic/ValidationIndicator.vue'

export default {
  name: 'CustomerAddress',
  mixins: [slotProxy(), transaction('personalData'), validationMixin, isValid],
  data() {
    return {
      currentCustomer: {
        salutation: '',
        firstName: '',
        lastName: '',
        address: {
          city: '',
          houseNumber: '',
          street: '',
          supplement: '',
          zip: ''
        }
      },
      isEditing: false,
      loading: false,
      backup: {}
    }
  },
  components: { ValidationIndicator },
  methods: {
    ...mapActions('personalData', ['changePersonalData']),
    save() {
      if (this.loading) return

      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$nextTick(() => {
          const firstError = document.querySelector('.input--invalid')
          const checkboxError = document.querySelector('.checkbox-class')
          if (firstError) firstError.focus()
          if (checkboxError && !firstError) checkboxError.focus()
        })
      } else {
        this.loading = true
        this.changePersonalData(this.currentCustomer).then(
          () => {
            this.loading = false
            this.isEditing = false
            this.$v.$reset()
          },
          () => {
            this.loading = false
            this.$v.$reset()
          }
        )
      }
    },
    discard() {
      this.isEditing = false
      this.$v.$reset()
      this.currentCustomer = this.backup
    },
    startEditing() {
      this.backup = JSON.parse(JSON.stringify(this.currentCustomer))
      this.isEditing = true
    }
  },
  props: {
    processName: {
      type: String,
      default: ''
    }
  },
  apollo: {
    currentCustomer: function() {
      const processName = this.processName
      return wrapQuery(
        gql`query ${processName} {
          currentCustomer {
            salutation,
            firstName,
            lastName,
            address {
              city,
              houseNumber,
              street,
              supplement,
              zip
            }
          }
        }`,
        this,
        'currentCustomer',
        {},
        null,
        {
          fetchPolicy: 'cache-first'
        }
      )
    }
  },
  validations() {
    return {
      currentCustomer: {
        address: {
          street: {
            required
          },
          houseNumber: {
            required,
            alphaNum,
            maxLength: maxLength(9)
          },
          zip: {
            validZipCode,
            required
          },
          city: {
            validCityString,
            required
          }
        }
      }
    }
  }
}
</script>
